import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { ArrowIosDownOutline } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn, css } from '@lyearn/style';
import { Box, Dot, Stack, Typography } from '@lyearn/ui';
import { RecommendedActionStatusEnum } from '@/types/schema';

import { InsightsRecommendationItem, UpdateRecommendationStatusProps } from '../types';
import { getActionTargetTitle } from '../utils';
import InsightRowAction from './InsightRowAction';
import InsightRowIcon from './InsightRowIcon';
import InsightRowReasons from './InsightRowReasons';
import StatusActionMenu from './StatusActionMenu';

const rotateButtonIcon = (degrees: number) => css`
  transition: transform 0.2s ease;
  transform: rotate(${degrees}deg);
`;

interface Props {
  recommendation: InsightsRecommendationItem;
  updateRecommendationStatus: (props: UpdateRecommendationStatusProps) => void;
  index: number;
}

function InsightRow(props: Props) {
  const { recommendation, updateRecommendationStatus } = props;

  const [showReasons, { toggle: toggleReasons }] = useToggle();

  const hasReasons = recommendation.card?.reasons && recommendation.card?.reasons.length > 0;

  const onRowClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      // Prevent toggling reasons when clicking on action button
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      updateRecommendationStatus({
        recommendationId: recommendation.id,
        status: RecommendedActionStatusEnum.Seen,
      });

      if (hasReasons) {
        toggleReasons();
      }
    },
    [hasReasons, toggleReasons, recommendation, updateRecommendationStatus],
  );

  return (
    <Stack
      key={recommendation.id}
      className={cn('px-16 py-20', 'bg-white', showReasons ? '' : ' hover:bg-bg-hover-light')}
      direction="col"
      onClick={onRowClick}>
      <Stack alignItems="center" direction="row" justifyContent="between">
        <Stack className="flex-1 min-w-0" direction="row" gapX={8}>
          <Box className="shrink-0">
            <InsightRowIcon recommendation={recommendation} />
          </Box>
          <Stack className="ms-8 me-12 flex-1 min-w-0" direction="col" gap={2}>
            <TypographyWithTooltip
              className="font-medium truncate"
              color="text-text-primary"
              numberOfLines={1}
              title={recommendation.card?.title}
              variant="label-02">
              {recommendation.card?.title}
            </TypographyWithTooltip>
            <Stack alignItems="center" className="min-w-0" direction="row" gapX={6}>
              <Stack alignItems="baseline" className="flex-1 min-w-0" direction="row" gapX={6}>
                <Typography className="text-[#A1863C] font-medium shrink-0" variant="label-03">
                  {getActionTargetTitle(recommendation.targetModule)}
                </Typography>
                <Dot className="w-4 h-4 p-2 mb-[0.3rem] self-center shrink-0" />
                <Stack className="flex-1 min-w-0" direction="row" gapX={6}>
                  <TypographyWithTooltip
                    className="font-normal truncate"
                    color="text-text-secondary"
                    title={recommendation.card?.subTitle}
                    variant="body-short-03">
                    {recommendation.card?.subTitle}
                  </TypographyWithTooltip>
                  {hasReasons && (
                    <Stack className="cursor-pointer shrink-0">
                      <ArrowIosDownOutline
                        className={cn('w-18 h-18', rotateButtonIcon(showReasons ? -180 : 0))}
                        fillColorToken="icon-secondary"
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack alignItems="center" className="shrink-0" direction="row" gapX={10}>
          <InsightRowAction recommendation={recommendation} />
          <StatusActionMenu
            recommendation={recommendation}
            updateRecommendationStatus={updateRecommendationStatus}
          />
          <Box
            className={cn(
              'w-8 h-8 rounded-full bg-icon-error shrink-0',
              recommendation.status === RecommendedActionStatusEnum.New ? '' : 'opacity-0',
            )}
          />
        </Stack>
      </Stack>

      {hasReasons && showReasons ? (
        <Box className="relative ms-24">
          {/* Curved corner using border radius - extends downward to connect */}
          <Box
            className="absolute left-[0px] top-0 w-[1px] h-[25px]"
            style={{
              borderLeft: '1px solid var(--border-border)',
              borderBottomLeftRadius: '0px',
            }}
          />
          {/* Horizontal line from curve to reasons */}
          <Box className="ml-0">
            <InsightRowReasons
              className="mt-8"
              open={showReasons}
              recommendation={recommendation}
            />
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}

export default React.memo(InsightRow);
